import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "../lib/cn";

const pillButtonBaseClasses =
  "h-10 gap-2 rounded-[8px] border-0 px-6 py-0 font-medium text-sm leading-[150%] text-white backdrop-blur-[40px] [&_svg]:size-4";
const pillButtonIdleClasses =
  "bg-[linear-gradient(90deg,rgba(255,255,255,0.05)_0%,rgba(255,255,255,0.05)_100%),linear-gradient(90deg,rgba(16,16,16,0.15)_0%,rgba(16,16,16,0.15)_100%)] transition-[background-image,opacity] hover:bg-[linear-gradient(90deg,rgba(255,255,255,0.08)_0%,rgba(255,255,255,0.08)_100%),linear-gradient(90deg,rgba(16,16,16,0.15)_0%,rgba(16,16,16,0.15)_100%)] disabled:pointer-events-auto disabled:cursor-not-allowed disabled:opacity-40";
const pillButtonActiveClasses =
  "bg-[linear-gradient(90deg,rgba(255,255,255,0.15)_0%,rgba(255,255,255,0.15)_100%),linear-gradient(90deg,rgba(16,16,16,0.15)_0%,rgba(16,16,16,0.15)_100%)] transition-[background-image,opacity] hover:bg-[linear-gradient(90deg,rgba(255,255,255,0.18)_0%,rgba(255,255,255,0.18)_100%),linear-gradient(90deg,rgba(16,16,16,0.15)_0%,rgba(16,16,16,0.15)_100%)] disabled:pointer-events-auto disabled:cursor-not-allowed disabled:opacity-40";
const compactButtonBaseClasses =
  "h-10 gap-2 rounded-lg border-0 px-6 py-0 font-medium text-sm leading-[150%] [&_svg]:size-4";

function ButtonSpinner({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 18 18"
      className={cn("size-5 shrink-0 animate-spin", className)}
      aria-hidden="true"
    >
      <circle
        cx="9"
        cy="9"
        r="8.1"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        opacity="0.2"
      />
      <circle
        cx="9"
        cy="9"
        r="8.1"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeDasharray="22 29"
        transform="rotate(-90 9 9)"
      />
    </svg>
  );
}

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-x-2.5 whitespace-nowrap rounded-full border font-medium text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        accent: `${compactButtonBaseClasses} bg-twi-blue-200 text-twi-black hover:bg-twi-blue-400`,
        default: "border-black bg-black text-white",
        close:
          "border-transparent bg-twi-gray-750 text-twi-gray-400 hover:bg-twi-gray-700 hover:text-white",
        popover: "h-11 border-none bg-input",
        outline: "border-black bg-transparent text-black",
        "outline-white": "border bg-transparent text-white",
        secondary: `${compactButtonBaseClasses} bg-twi-gray-800 text-white hover:bg-twi-gray-700`,
        auth: "w-full border-0 bg-white font-medium text-base text-twi-black leading-6 hover:bg-white/90 disabled:opacity-100",
        "auth-inactive":
          "w-full border-0 bg-twi-gray-750 font-medium text-base text-twi-gray-500 leading-6 hover:bg-twi-gray-750 disabled:opacity-100",
        pill: `${pillButtonBaseClasses} ${pillButtonIdleClasses}`,
        "pill-active": `${pillButtonBaseClasses} ${pillButtonActiveClasses}`,
      },
      size: {
        default: "h-12 px-6 py-3",
        "icon-sm": "size-7 shrink-0 p-0",
        popover: "h-11",
        pill: "h-10 px-6 py-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  "data-testid"?: string;
  loading?: boolean;
  loadingLabel?: string;
  loadingSpinnerClassName?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      "data-testid": dataTestId,
      loading = false,
      loadingLabel,
      loadingSpinnerClassName,
      children,
      disabled,
      "aria-label": ariaLabel,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    // Determine accessible name during loading
    const loadingAccessibleLabel =
      loadingLabel ??
      ariaLabel ??
      (typeof children === "string" ? children : undefined);

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        data-testid={dataTestId ?? "ui-button"}
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        aria-label={loading ? loadingAccessibleLabel : ariaLabel}
        {...props}
      >
        {loading ? (
          <>
            <ButtonSpinner className={loadingSpinnerClassName} />
            {/* Preserve accessible name for screen readers */}
            {loadingAccessibleLabel && loadingAccessibleLabel !== ariaLabel ? (
              <span className="sr-only">{loadingAccessibleLabel}</span>
            ) : null}
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
